import React, { useEffect, useState } from "react";
import { toDateOnlyYmd } from "@mediatechgroup/calendar-date";

import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { getAthleteEnrollment } from "../../../api/athletesAPI";
import { endOfDay, format, parseISO, startOfDay, subWeeks } from "date-fns";
import { lastSevenDaysRange } from "../../../utils/attendanceDateRanges";
import PresetDateRangeFilter from "../../common/PresetDateRangeFilter";
import News from "../../../pages/News";
import { useTheme } from "../../../context/ThemeContext";
import { getPageBackground, getPrimaryText, getSecondaryText, getMutedText, getLightBorder } from "../../../utils/themeUtils";
import { captureError } from "../../../utils/sentry";
import { useViewportFlags } from "../../../hooks/useViewportFlags";
import { Card, SectionHeading } from "../../ui";

interface AttendanceRecord {
  attendance_date: string;
  status: "present" | "absent" | "no_record";
}

interface Enrollment {
  id: string;
  enrolled_at: string;
  program: { label: string };
  program_slot: { slot_id: string };
  attendance_records?: AttendanceRecord[];
}

interface Athlete {
  id: string;
  first_name: string;
  last_name: string;
  enrollments: Enrollment[];
}

interface Props {
  athletes: Athlete[];
}

const PrivateAthleteAttendance: React.FC<Props> = ({ athletes }) => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation(['dashboard', 'common']);

  // Define preset ranges inside component to access translations
  const presetRanges = [
    {
      label: t('attendance.last7Days'),
      getRange: () => lastSevenDaysRange(),
    },
    {
      label: t('attendance.last4Weeks'),
      getRange: () => {
        const end = endOfDay(new Date());
        const start = startOfDay(subWeeks(end, 4));
        return [start, end];
      },
    },
    {
      label: t('attendance.allTime'),
      getRange: () => {
        const end = endOfDay(new Date());
        const start = new Date(2000, 0, 1); // Jan 1, 2000 - captures all historical data
        return [start, end];
      },
    },
  ];
  const [enrollments, setEnrollments] = useState<{
    [athleteId: string]: any[];
  }>({});
  const [loading, setLoading] = useState(false);
  const { isMobileDevice } = useViewportFlags();
  const [selectedPreset, setSelectedPreset] = useState<string | null>(
    t('attendance.last4Weeks')
  );
  const [startDate, setStartDate] = useState<string>(
    format(subWeeks(new Date(), 4), "yyyy-MM-dd")
  );
  const [endDate, setEndDate] = useState<string>(
    format(new Date(), "yyyy-MM-dd")
  );
  const token = localStorage.getItem("authToken");


  useEffect(() => {
    async function fetchAllEnrollments() {
      setLoading(true);
      try {
        const allEnrollments: { [athleteId: string]: Enrollment[] } = {};

        // Per-iteration try/catch, NOT one around the loop. These are
        // independent lookups: with the API rethrowing, a single failure would
        // otherwise abort the loop, skip setEnrollments entirely, and lose
        // EVERY athlete's enrollments — not just the one that failed.
        for (const athlete of athletes) {
          try {
            const res = await getAthleteEnrollment(token, athlete?.id);
            if (res?.success) {
              allEnrollments[athlete.id] = res.data;
            } else {
              toast.error(
                t('common:messages.error')
              );
            }
          } catch (athleteErr) {
            captureError(athleteErr, { component: "PrivateAthleteAttendance", operation: "fetchEnrollment" }, "warning");
            toast.error(t('common:messages.error'));
          }
        }

        setEnrollments(allEnrollments);
      } catch (err) {
        toast.error(t('common:messages.error'));
        captureError(err, { component: "PrivateAthleteAttendance", operation: "fetchEnrollments" });
      } finally {
        setLoading(false);
      }
    }

    if (athletes?.length > 0) {
      fetchAllEnrollments();
    }
  }, []);

  return (
    <div className="w-full">
      {/* News Section */}
      <div className={`${
        isMobileDevice ? 'mb-4' : 'mb-6'
      }`}>
        <News embedded />
      </div>

      {/* Divider */}
      <div className={`border-t-2 ${getLightBorder(isDarkMode)} ${
        isMobileDevice ? 'mb-4' : 'mb-6'
      }`}></div>

      {/* Header */}
      <SectionHeading
        description={isMobileDevice ? t('attendance.viewAll') : undefined}
      >
        {t('sections.familyAttendance')}
      </SectionHeading>

      {/* Date Filter */}
      <div className={`${getPageBackground(isDarkMode)} rounded-lg p-4 border ${getLightBorder(isDarkMode)} ${
        isMobileDevice ? 'mb-5' : 'mb-6'
      }`}>
        <PresetDateRangeFilter
          presetRanges={presetRanges}
          selectedPreset={selectedPreset}
          setSelectedPreset={setSelectedPreset}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />
      </div>

      {loading ? (
        <div
          className="w-full flex justify-center items-center"
          style={{ height: "calc(100vh - 450px)" }}
        >
          <div className="flex justify-center items-center mb-4">
            <div className="w-6 h-6 border-2 border-gray-400 dark:border-gray-500 border-t-transparent rounded-full animate-spin"></div>
            <span className="ml-2 text-gray-500 dark:text-gray-400">{t('attendance.loading')}</span>
          </div>
        </div>
      ) : athletes.length === 0 ? (
        <p className={getPrimaryText(isDarkMode)}>{t('attendance.noAthletes')}</p>
      ) : (
        athletes?.map((athlete) => {
          const athleteEnrollments = enrollments[athlete.id] || [];

          return (
            <div key={athlete.id} className="mb-8">
              <SectionHeading as="h3">
                {athlete.first_name} {athlete.last_name}
              </SectionHeading>

              {athleteEnrollments.length === 0 ? (
                <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>
                  {t('attendance.noPrograms')}
                </p>
              ) : (
                athleteEnrollments.map((enrollment) => {
                  const filteredRecords = (enrollment.attendance_records || [])
                    // Calendar days compared as YYYY-MM-DD (the range is
                    // yyyy-MM-dd too). Parsing both sides as UTC instants
                    // happened to agree; this cannot drift.
                    .filter((record: any) => {
                      const day = toDateOnlyYmd(record.attendance_date);
                      return day >= startDate && day <= endDate;
                    })
                    .sort((a: any, b: any) =>
                      toDateOnlyYmd(b.attendance_date).localeCompare(toDateOnlyYmd(a.attendance_date))
                    );

                  return (
                    <Card
                      key={enrollment.id}
                      padding="sm"
                      className="mb-6"
                    >
                      <h4 className={`text-md font-semibold ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                        {enrollment.program.label}
                      </h4>
                      <p className={`text-sm ${getSecondaryText(isDarkMode)}`}>
                        {t('attendance.slotId')} {enrollment.program_slot.slot_id}
                      </p>
                      <p className={`text-sm mb-2 ${getSecondaryText(isDarkMode)}`}>
                        {t('attendance.enrolledAt')}{" "}
                        {new Date(enrollment.enrolled_at).toLocaleString()}
                      </p>

                      <div className="mt-4">
                        <h5 className={`text-md font-medium mb-2 ${getPrimaryText(isDarkMode)}`}>
                          {t('attendance.attendanceRecords')}
                        </h5>
                        {filteredRecords.length === 0 ? (
                          <p className={`text-sm ${getMutedText(isDarkMode)}`}>
                            {t('attendance.noAttendanceInRange')}
                          </p>
                        ) : (
                          <ul className="space-y-1 text-sm max-h-40 overflow-y-auto pr-2">
                            {filteredRecords.map((record: any, index: any) => {
                              const isPresent = record.status === "present";
                              const isNoRecord = record.status === "no_record";

                              const statusColor = isPresent
                                ? (isDarkMode ? "text-green-400" : "text-green-600")
                                : isNoRecord
                                  ? (isDarkMode ? "text-gray-400" : "text-gray-500")
                                  : (isDarkMode ? "text-red-400" : "text-red-500");

                              const statusText = isPresent
                                ? t('attendance.present')
                                : isNoRecord
                                  ? t('attendance.noRecord', 'No Record')
                                  : t('attendance.absent');

                              return (
                                <li
                                  key={index}
                                  className={`flex justify-between border-b ${getLightBorder(isDarkMode)} py-1`}
                                >
                                  <span className={getSecondaryText(isDarkMode)}>
                                    {format(parseISO(record.attendance_date), 'M/d/yyyy')}
                                  </span>
                                  <span className={`font-semibold ${statusColor}`}>
                                    {statusText}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </div>
                    </Card>
                  );
                })
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

export default PrivateAthleteAttendance;
